import { Form, useFormikContext } from "formik";
import { RoleSelector } from "@/core/components/role-selector";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { USER_ROLES } from "@/user/constants";
import type { DetailedUser } from "@/user/user";
import type { UserAccountValues } from "./types";

export const UserAccountFields = ({
  account,
  onCancel,
  actionsAlign = "start",
  allowTechnicianConversion = false,
}: {
  account?: DetailedUser;
  onCancel: () => void;
  actionsAlign?: "start" | "end";
  allowTechnicianConversion?: boolean;
}) => {
  const {
    values,
    getFieldProps,
    setFieldValue,
    dirty,
    isSubmitting,
    touched,
    errors,
  } = useFormikContext<UserAccountValues>();
  const { settings } = useBusinessProducts();
  const canConvertToTechnician =
    allowTechnicianConversion &&
    account?.parentable_type === "Business" &&
    account.role !== USER_ROLES.ADMIN;
  const cancelButton = (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      disabled={isSubmitting}
      onClick={onCancel}
    >
      Cancel
    </Button>
  );
  return (
    <Form>
      <Stack gap={4} className="w-full">
        <Stack direction="row" gap={4} className="w-full">
          <FormInput
            label="First name"
            disabled={isSubmitting}
            fullWidth
            {...getFieldProps("first_name")}
          />
          <FormInput
            label="Last name"
            disabled={isSubmitting}
            fullWidth
            {...getFieldProps("last_name")}
          />
        </Stack>
        <FormInput
          label="Email"
          disabled={isSubmitting}
          type="email"
          required
          fullWidth
          {...getFieldProps("email")}
        />
        <RoleSelector
          value={values.role}
          originalRole={account?.role}
          productSettings={settings}
          disabled={isSubmitting}
          onChange={(role) => void setFieldValue("role", role)}
          exclude={
            canConvertToTechnician
              ? [USER_ROLES.ADMIN]
              : [USER_ROLES.ADMIN, USER_ROLES.TECH]
          }
          required
          fullWidth
        />
        {touched.role && errors.role && (
          <Alert color="danger">{errors.role}</Alert>
        )}
        {canConvertToTechnician && values.role === USER_ROLES.TECH && (
          <Alert color="warning">
            This user will become a technician. After you save, they will no
            longer appear in User Management. Add them to the payroll roster
            separately. Only a Volca admin can change them back.
          </Alert>
        )}
        <Stack
          direction="row"
          gap={2}
          justify={actionsAlign}
          className="w-full"
        >
          {actionsAlign === "end" && cancelButton}
          <Button
            type="submit"
            size="sm"
            disabled={!dirty || isSubmitting}
            loading={isSubmitting}
            loadingLabel={account ? "Saving account" : "Creating account"}
          >
            {account ? "Save changes" : "Create user"}
          </Button>
          {actionsAlign === "start" && cancelButton}
        </Stack>
      </Stack>
    </Form>
  );
};
